import {Injectable, NgZone} from '@angular/core';
import { hymnsDetailed } from "../hymn/hymnsDetailed";
import {Router} from "@angular/router";
import {MidiPlayerService} from "./midi-player.service";
import {HymnSelectionService} from "./hymn-selection.service";
import {LayoutService} from "./layout.service";
import {SidenavService} from "./sidenav.service";
import {SettingsService} from "./settings.service";
import {AutoScrollService} from "./auto-scroll.service";
import {translation} from "../translation";

@Injectable({
  providedIn: 'root'
})
export class ContinuousPlaybackService {

  playlist: any[] = [];
  hymnEndListener: any;
  active = false;

  constructor(private router: Router,
              private midiPlayerService: MidiPlayerService,
              private hymnSelectionService: HymnSelectionService,
              private layoutService: LayoutService,
              private sideNavService: SidenavService,
              private settingsService: SettingsService,
              private zone: NgZone,
              private autoScrollService: AutoScrollService) {
  }

  async shuffle(array: string[]) {
    let currentIndex = array.length;

    // While there remain elements to shuffle...
    while (currentIndex != 0) {

      // Pick a remaining element...
      let randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]];
    }

    return array;
  }

  index = 0;
  async startContinuousPlayback(favoritesOnly: boolean = false) {
    if(favoritesOnly) {
      this.playlist = this.settingsService.getFavorites().filter(hymn => hymn.hasMidi);
    } else {
      this.playlist = hymnsDetailed.filter(hymn => hymn.hasMidi);
    }

    await this.shuffle(this.playlist);
    console.debug('Starting continuous playback');

    this.settingsService.setHymnViewMode();

    // Force autoscroll during continuous playback (without altering the user's setting)
    this.active = true;
    this.autoScrollService.forceOn();

    this.index = 0;

    this.hymnEndListener = this.midiPlayerService.status$.subscribe( status => {
      this.zone.run(() => {
        if(this.active && status == 'ended') {
          this.index++;
          if (this.index >= this.playlist.length) {
            this.index = 0;
          }
          this.playHymn(this.index, 2000);
        }
      });
    });
    this.playHymn(this.index);
    this.midiPlayerService.setPreviousButtonAction(() => this.previous());
    this.midiPlayerService.setNextButtonAction(() => this.next());
  }

  async stopContinuousPlayback() {
    this.zone.run(() => {
      // Stop forced autoscroll for continuous playback
      this.autoScrollService.stopAutoScroll();
      this.autoScrollService.forceOff();

      // Restore autoscroll enabled state according to the current user setting (do not change the setting)
      if (this.settingsService.getAutoscroll()) {
        this.autoScrollService.enableAutoScroll();
      } else {
        this.autoScrollService.disableAutoScroll();
      }

      if (this.hymnEndListener) {
        this.hymnEndListener.unsubscribe();
      }
      this.active = false;
      this.midiPlayerService.stop();
      this.midiPlayerService.clearPreviousButtonAction();
      this.midiPlayerService.clearNextButtonAction();
    });
  }

  startTimeout: any;

  async playHymn(index: number, waitDuration: number = 2000) {
    const playing: boolean = this.startTimeout || this.midiPlayerService.playing;
    clearTimeout(this.startTimeout);

    this.midiPlayerService.stop();

    console.debug('Playing ' + this.playlist[index]);
    await this.router.navigate([`/${translation.path_hymn}`, this.playlist[index].hymnNumber]);
    this.sideNavService.closeList();

    const container = document.querySelector('.mat-sidenav-content')!;
    this.autoScrollService.scrollToTop();

    this.startTimeout = setTimeout(() => {
      this.midiPlayerService.playMidi(
        this.playlist[index],
        this.settingsService.getInstruments(),
        this.playlist[index].numberOfVerses,
        this.midiPlayerService.playbackSpeed
      );
    }, playing ? waitDuration : 0);
  }

  next() {
    this.index++;
    if (this.index >= this.playlist.length) {
      this.index = 0;
    }
    this.playHymn(this.index);
  }

  previous() {
    this.index--;
    if (this.index < 0) {
      this.index = this.playlist.length - 1;
    }
    this.playHymn(this.index);
  }
}
